import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, LayoutGrid, List, Search } from "lucide-react";
import api, { money, dateShort } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Pill, Progress, Thumb, EmptyState, Cover } from "@/components/Bits";

const STATUS_FILTERS = ["Active", "Lead", "On Hold", "Completed", "Archived"];
const TYPE_FILTERS = ["Residential Interior", "Commercial Interior", "Experiential", "Set Design", "Seasonal Installation", "Event Installation", "Other"];

export default function Projects() {
  const { isStaff } = useAuth();
  const [urlParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [view, setView] = useState("grid");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(urlParams.get("status") || "");
  const [type, setType] = useState("");
  const [clientScope, setClientScope] = useState("current");

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);

  const filtered = useMemo(() => {
    if (!isStaff) {
      const completed = (p) => ["Complete", "Completed", "Archived"].includes(p.status);
      return projects.filter((p) => (clientScope === "completed" ? completed(p) : !completed(p)));
    }
    return projects.filter(
      (p) =>
        (!status || p.status === status) &&
        (!type || p.project_type === type) &&
        (!q ||
          (p.name || "").toLowerCase().includes(q.toLowerCase()) ||
          (p.client_name || "").toLowerCase().includes(q.toLowerCase()))
    );
  }, [projects, q, status, type, isStaff, clientScope]);

  return (
    <div className="space-y-10" data-testid="projects-page">
      <PageHeader
        overline={isStaff ? "Studio" : "Your Projects"}
        title="Projects"
        subtitle={isStaff ? "Every engagement, from lead to install." : "Everything we’re designing for you."}
        actions={
          <>
            <div className="flex rounded-sm border border-line bg-white">
              <button
                data-testid="projects-grid-view-btn"
                onClick={() => setView("grid")}
                className={`p-2.5 transition-colors ${view === "grid" ? "bg-[#EDEFC4] text-ink" : "text-stone hover:text-ink"}`}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" strokeWidth={1.5} />
              </button>
              <button
                data-testid="projects-list-view-btn"
                onClick={() => setView("list")}
                className={`p-2.5 transition-colors ${view === "list" ? "bg-[#EDEFC4] text-ink" : "text-stone hover:text-ink"}`}
                aria-label="List view"
              >
                <List className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
            {isStaff && (
              <Link
                data-testid="new-project-btn"
                to="/projects/new"
                className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark"
              >
                <Plus className="h-4 w-4" strokeWidth={1.5} /> New Project
              </Link>
            )}
          </>
        }
      />

      {isStaff ? (
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="relative lg:w-80">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fog" strokeWidth={1.5} />
          <input
            data-testid="projects-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search projects or clients"
            className="w-full rounded-sm border border-line bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-lime"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              data-testid={`filter-status-${s.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setStatus(status === s ? "" : s)}
              className={`rounded-sm border px-3 py-1.5 text-xs transition-colors ${
                status === s ? "border-lime bg-lime text-ink" : "border-line bg-white text-stone hover:border-fog"
              }`}
            >
              {s}
            </button>
          ))}
          <select
            data-testid="filter-type-select"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="rounded-sm border border-line bg-white px-3 py-1.5 text-xs text-stone outline-none focus:border-lime"
          >
            <option value="">All types</option>
            {TYPE_FILTERS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>
      ) : (
        <div className="flex rounded-sm border border-line bg-white" data-testid="client-project-filters">
          {[
            ["current", "Current"],
            ["completed", "Completed"],
          ].map(([value, label]) => (
            <button
              key={value}
              data-testid={`client-projects-filter-${value}`}
              onClick={() => setClientScope(value)}
              className={`px-5 py-2.5 text-xs uppercase tracking-[0.12em] transition-colors ${
                clientScope === value ? "bg-[#EDEFC4] text-ink" : "text-stone hover:text-ink"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      {filtered.length === 0 ? (
        <EmptyState testId="projects-empty" title="No projects match" body="Adjust your filters or create a new project." />
      ) : view === "grid" ? (
        <div className="grid gap-7 md:grid-cols-2 xl:grid-cols-3" data-testid="projects-grid">
          {filtered.map((p, i) => (
            <Link
              key={p.id}
              to={`/projects/${p.id}`}
              data-testid={`project-card-${p.id}`}
              className="card-surface card-hover group animate-rise overflow-hidden"
              style={{ animationDelay: `${i * 50}ms` }}
            >
              <div className="aspect-[16/10] overflow-hidden bg-line">
                <Cover project={p} testId={`project-card-cover-${p.id}`} className="h-full w-full" imgClassName="transition-transform duration-500 group-hover:scale-[1.03]" />
              </div>
              <Progress value={p.progress} />
              <div className="p-6">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate font-serif text-xl text-ink">{p.name}</h3>
                    {isStaff && <div className="mt-1 truncate text-sm text-stone">{p.client_name || "No client"}</div>}
                  </div>
                  <Pill tone={p.status === "Active" || p.status === "In Progress" ? "bg-lime text-ink" : "bg-line text-stone"}>{p.status}</Pill>
                </div>
                {isStaff ? (
                  <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                    <span className="overline">{p.project_type}</span>
                    <Pill tone="bg-[#EDEFC4] text-ink">{p.phase}</Pill>
                  </div>
                ) : (
                  <div className="mt-4 text-sm text-stone">
                    <span className="overline block">Current Phase</span>
                    <span className="mt-1 block text-ink">{p.phase}</span>
                  </div>
                )}
                {isStaff ? (
                <div className="mt-5 grid grid-cols-2 gap-4 border-t border-line pt-5 text-xs">
                  <div>
                    <div className="overline">Budget</div>
                    <div className="num mt-1 text-sm text-ink">{money(p.budget_total)}</div>
                  </div>
                  <div>
                    <div className="overline">Target</div>
                    <div className="num mt-1 text-sm text-stone">{dateShort(p.target_completion_date)}</div>
                  </div>
                </div>
                ) : (
                <div className="mt-5 border-t border-line pt-5 text-xs">
                  <div className="overline">{p.install_date ? "Installation" : "Target Completion"}</div>
                  <div className="num mt-1 text-sm text-ink">{dateShort(p.install_date || p.target_completion_date)}</div>
                </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="card-surface overflow-x-auto scroll-thin" data-testid="projects-list">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                {["Project", "Client", "Type", "Status", "Phase", "Budget", "Approved", "Target"].map((h) => (
                  <th key={h} className="overline px-5 py-4 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-line transition-colors last:border-0 hover:bg-bone">
                  <td className="px-5 py-4">
                    <Link to={`/projects/${p.id}`} data-testid={`project-row-${p.id}`} className="flex items-center gap-3 text-ink hover:text-lime-dark">
                      <Cover project={p} className="h-9 w-12" rounded="rounded-sm" />
                      <span className="font-medium">{p.name}</span>
                    </Link>
                  </td>
                  <td className="px-5 py-4 text-stone">{p.client_name || "—"}</td>
                  <td className="px-5 py-4 text-stone">{p.project_type}</td>
                  <td className="px-5 py-4"><Pill tone={p.status === "Active" ? "bg-lime text-ink" : "bg-line text-stone"}>{p.status}</Pill></td>
                  <td className="px-5 py-4 text-stone">{p.phase}</td>
                  <td className="num px-5 py-4 text-right">{money(p.budget_total)}</td>
                  <td className="num px-5 py-4 text-right text-lime-dark">{money(p.approved_spend)}</td>
                  <td className="num px-5 py-4 text-stone">{dateShort(p.target_completion_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
